import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import {
  OrbiAgentCardActionEvent,
  OrbiAgentEntryState,
  OrbiAgentInputMethod,
  PendoService,
  selectOrbiMessages,
  selectOrbiSessionId,
} from '@patient-ui/patient-web/store';
import { OrbiMessageType } from '@patient-ui/shared/enums';
import { OrbiMessage } from '@patient-ui/shared/models';

import {
  ORBI_AGENT_UNKNOWN,
  buildOrbiResponseRenderedEvent,
  resolveOrbiSourcePage,
} from '../utils/orbi-track-event.util';

export type OrbiCardActionInput = Omit<OrbiAgentCardActionEvent, 'session_id'>;

@Injectable({ providedIn: 'root' })
export class OrbiTrackingService {
  private lastOpenedKey: string | null = null;

  constructor(
    private readonly pendoService: PendoService,
    private readonly store: Store,
    private readonly router: Router
  ) {}

  trackAgentOpened(entryState: OrbiAgentEntryState): void {
    this.report(() => {
      const sourcePage = resolveOrbiSourcePage(this.router.url);
      const openedKey = `${entryState}|${sourcePage}`;
      if (entryState === 'new' && openedKey === this.lastOpenedKey) {
        return;
      }

      this.lastOpenedKey = openedKey;
      this.pendoService.trackAgentOpened({
        source_page: sourcePage,
        entry_state: entryState,
      });
    });
  }

  trackMessageSent(inputMethod: OrbiAgentInputMethod): void {
    this.lastOpenedKey = null;
    this.withSessionId((sessionId) => {
      const sent = this.sentMessageCount();
      if (sent === undefined) {
        return;
      }
      this.pendoService.trackAgentMessageSent({
        message_index: sent + 1,
        input_method: inputMethod,
        session_id: sessionId,
      });
    });
  }

  trackResponseRendered(message: OrbiMessage): void {
    this.withSessionId((sessionId) =>
      this.pendoService.trackAgentResponseRendered(
        buildOrbiResponseRenderedEvent(message, sessionId)
      )
    );
  }

  trackErrorRendered(): void {
    this.withSessionId((sessionId) =>
      this.pendoService.trackAgentResponseRendered({
        response_type: 'error',
        agent: ORBI_AGENT_UNKNOWN,
        session_id: sessionId,
      })
    );
  }

  trackCardAction(action: OrbiCardActionInput): void {
    this.withSessionId((sessionId) =>
      this.pendoService.trackAgentCardAction({
        ...action,
        session_id: sessionId,
      })
    );
  }

  private withSessionId(track: (sessionId: string) => void): void {
    this.report(() => {
      const sessionId = this.currentSessionId();
      if (sessionId === undefined) {
        return;
      }
      track(sessionId);
    });
  }

  private report(track: () => void): void {
    try {
      track();
    } catch {
      return;
    }
  }

  private currentSessionId(): string | undefined {
    let sessionId: string | undefined;
    this.store
      .select(selectOrbiSessionId)
      .pipe(take(1))
      .subscribe({
        next: (value) => (sessionId = value ?? ''),
        error: () => undefined,
      });
    return sessionId;
  }

  private sentMessageCount(): number | undefined {
    let sent: number | undefined;
    this.store
      .select(selectOrbiMessages)
      .pipe(take(1))
      .subscribe({
        next: (messages) => {
          sent = (messages ?? []).filter(
            (message) => message.type === OrbiMessageType.User
          ).length;
        },
        error: () => undefined,
      });
    return sent;
  }
}
